type ValidationResult = { ok: true } | { ok: false; error: string };

// Format-only patterns (no checksum). Covers all 27 EU member states.
// Greece uses the VAT prefix "EL" (not the ISO code GR).
const EU_VAT_PATTERNS: Record<string, RegExp> = {
  AT: /^ATU\d{8}$/,
  BE: /^BE[01]\d{9}$/,
  BG: /^BG\d{9,10}$/,
  CY: /^CY\d{8}[A-Z]$/,
  CZ: /^CZ\d{8,10}$/,
  DE: /^DE\d{9}$/,
  DK: /^DK\d{8}$/,
  EE: /^EE\d{9}$/,
  EL: /^EL\d{9}$/,
  ES: /^ES[A-Z0-9]\d{7}[A-Z0-9]$/,
  FI: /^FI\d{8}$/,
  FR: /^FR[A-Z0-9]{2}\d{9}$/,
  HR: /^HR\d{11}$/,
  HU: /^HU\d{8}$/,
  IE: /^(?:IE\d{7}[A-Z]{1,2}|IE\d[A-Z]\d{5}[A-Z])$/,
  IT: /^IT\d{11}$/,
  LT: /^LT(?:\d{9}|\d{12})$/,
  LU: /^LU\d{8}$/,
  LV: /^LV\d{11}$/,
  MT: /^MT\d{8}$/,
  NL: /^NL\d{9}B\d{2}$/,
  PL: /^PL\d{10}$/,
  PT: /^PT\d{9}$/,
  RO: /^RO\d{2,10}$/,
  SE: /^SE\d{12}$/,
  SI: /^SI\d{8}$/,
  SK: /^SK\d{10}$/,
};

const FRIENDLY_FORMATS: Record<string, string> = {
  AT: "AT + U + 8 digits",
  BE: "BE + 0 or 1 + 9 digits",
  BG: "BG + 9 or 10 digits",
  CY: "CY + 8 digits + letter",
  CZ: "CZ + 8–10 digits",
  DE: "DE + 9 digits",
  DK: "DK + 8 digits",
  EE: "EE + 9 digits",
  EL: "EL + 9 digits",
  ES: "ES + 9 chars (mix of letters and digits)",
  FI: "FI + 8 digits",
  FR: "FR + 2 chars + 9 digits",
  HR: "HR + 11 digits",
  HU: "HU + 8 digits",
  IE: "IE + 7 digits + 1–2 letters (or alt format)",
  IT: "IT + 11 digits",
  LT: "LT + 9 or 12 digits",
  LU: "LU + 8 digits",
  LV: "LV + 11 digits",
  MT: "MT + 8 digits",
  NL: "NL + 9 digits + B + 2 digits",
  PL: "PL + 10 digits",
  PT: "PT + 9 digits",
  RO: "RO + 2–10 digits",
  SE: "SE + 12 digits",
  SI: "SI + 8 digits",
  SK: "SK + 10 digits",
};

export function validateEuVatId(raw: string): ValidationResult {
  const input = raw.replace(/\s/g, "").toUpperCase();
  if (!input) return { ok: false, error: "VAT ID is required" };

  const prefix = input.slice(0, 2);
  const pattern = EU_VAT_PATTERNS[prefix];
  if (!pattern) {
    return {
      ok: false,
      error: "Unknown country prefix — VAT ID must start with an EU country code",
    };
  }
  if (!pattern.test(input)) {
    return {
      ok: false,
      error: `Invalid format for ${prefix} (expected ${FRIENDLY_FORMATS[prefix]})`,
    };
  }
  return { ok: true };
}
